// SignageAddDynamicCost.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { components } from "react-select";
import Select from "react-select";


/* --------------------------- Styled reusable children --------------------------- */
type SimpleOption = { value: string; label: string };
/** Option with optional isDisabled (e.g. for out-of-stock attribute values). */
export type SimpleOptionWithDisabled = SimpleOption & { isDisabled?: boolean };
type BaseFieldProps = {
  formik: ReturnType<typeof useFormik<any>>;
  name: string;
  label: string | React.ReactNode;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string; // Optional id override (defaults to name if not provided)
  autocomplete?: string;
  spellCheck?: boolean;
  autoCorrect?: string;
  autoCapitalize?: string;
  ignorePasswordManagers?: boolean;
};

type BaseFieldProps1 = {
  formik: ReturnType<typeof useFormik<any>>;
  name: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;   // ✅ add this
  type?: string;
  id?: string; // Optional id override (defaults to name if not provided)
  autocomplete?: string; // e.g. "off" to prevent browser autofill
  spellCheck?: boolean;
  autoCorrect?: string;
  /** Hints LastPass / 1Password / Bitwarden not to offer autofill on admin identity fields. */
  ignorePasswordManagers?: boolean;
};
type UIReactSelectPOPProps = {
  name: string;
  value: SimpleOption | null;
  options: SimpleOptionWithDisabled[];
  placeholder?: string;
  menuMaxHeight?: number;
  menuPortalZIndex?: number;
  onChange?: (opt: SimpleOption | null) => void;
  className?: string;
};

/** Hide arrow when disabled */
const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

const isEmailFieldName = (name?: string) => /email/i.test(String(name || ""));
const resolveAutocomplete = (value?: string, fallbackOff = false) => {
  const requested = value == null || String(value).trim() === ""
    ? (fallbackOff ? "off" : undefined)
    : String(value).trim();
  return requested?.toLowerCase() === "off" ? "nope" : requested;
};

const customSelectStyles = {
  /* hide all indicators when disabled */
  indicatorsContainer: (provided: any, state: any) => ({
    ...provided,
    display: state.isDisabled ? "none" : "flex",
  }),
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    display: state.isDisabled ? "none" : "flex",
  }),
  indicatorSeparator: () => ({ display: "none" }),

  /* keep bg and cursor right; do NOT fade */
  control: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isDisabled ? "#f2f2f2" : provided.backgroundColor,
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),

  /* ——— make text BLACK when disabled ——— */
  singleValue: (provided: any, state: any) => ({
    ...provided,
    color: state.isDisabled ? "#000" : provided.color,
  }),
  valueContainer: (provided: any, state: any) => ({
    ...provided,
    color: state.isDisabled ? "#000" : provided.color,
  }),
  input: (provided: any, state: any) => ({
    ...provided,
    color: state.isDisabled ? "#000" : provided.color,
    WebkitTextFillColor: state.isDisabled ? "#000" : undefined, // Safari
  }),
  placeholder: (provided: any, state: any) => ({
    ...provided,
    /* if field has no value, keep placeholder readable too */
    color: state.isDisabled ? "#000" : provided.color,
  }),

  /* if you ever use isMulti, keep chips readable */
  multiValue: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor,
  }),
  multiValueLabel: (p: any, s: any) => ({
    ...p,
    color: s.isDisabled ? "#000" : p.color,
  }),
  multiValueRemove: (p: any, s: any) => ({
    ...p,
    display: s.isDisabled ? "none" : p.display,
  }),
};


const SELECT_HEIGHT = 39;

const commonTheme = (t: any) => ({
  ...t,
  colors: {
    ...t.colors,
    primary: "#9ca3af",
    primary25: "rgba(156,163,175,0.15)",
    primary50: "rgba(156,163,175,0.25)",
  },
});

const centeredCommonStyles = (hasError: boolean) => ({
  control: (base: any, state: any) => ({
    ...base,
    minHeight: SELECT_HEIGHT,
    height: SELECT_HEIGHT,           // fixed for single-select
    borderRadius: 10,
    alignItems: "center",
    backgroundColor: state.isFocused ? "#fafafa" : "#f5f6fa",
    borderWidth: 2,
    borderStyle: "solid",
    borderColor: hasError ? "#747981" : state.isFocused ? "#9ca3af" : "#c9ced6",
    boxShadow: "none",
    outline: "none",
    transition: "all .2s ease-in-out",
    ":hover": {
      borderColor: hasError ? "#747981" : "#9ca3af",
      backgroundColor: "#fafafa",
    },
  }),
  valueContainer: (b: any) => ({
    ...b,
    padding: "0 12px",
    minHeight: SELECT_HEIGHT,
    display: "flex",
    alignItems: "center",
  }),
  indicatorsContainer: (b: any) => ({
    ...b,
    paddingRight: 10,
    minHeight: SELECT_HEIGHT,
    display: "flex",
    alignItems: "center",
  }),
  dropdownIndicator: (provided: any) => ({
    ...provided,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  placeholder: (b: any) => ({
    ...b,
    color: "#6b7280",
    margin: 0,
    display: "flex",
    alignItems: "center",
  }),
  singleValue: (b: any) => ({
    ...b,
    color: "#111827",
    margin: 0,
    display: "flex",
    alignItems: "center",
  }),
  input: (b: any) => ({
    ...b,
    margin: 0,
    padding: 0,
  }),
  menu: (b: any) => ({ ...b, borderRadius: 10, overflow: "hidden" }),
});

// For multi-select (chips can wrap so control height must be auto)
const centeredMultiStyles = (hasError: boolean) => {
  const base = centeredCommonStyles(hasError);
  return {
    ...base,
    control: (b: any, state: any) => ({
      ...base.control(b, state),
      height: "auto",               // allow chip rows
      minHeight: SELECT_HEIGHT,
    }),
    valueContainer: (b: any) => ({
      ...base.valueContainer(b),
      padding: "4px 12px",
    }),
    multiValue: (b: any) => ({
      ...b,
      backgroundColor: "rgba(156,163,175,0.15)",
    }),
    multiValueLabel: (b: any) => ({
      ...b,
      color: "#111827",
      paddingRight: 2,
    }),
    multiValueRemove: (b: any) => ({
      ...b,
      ":hover": { backgroundColor: "rgba(156,163,175,0.25)", cursor: "pointer" },
    }),
  };
};

export function UIInputDisable({
  formik,
  name,
  label,
  required,
  placeholder,
  disabled = false,
  type = "text",
  id,
  autocomplete,
  spellCheck,
  autoCorrect,
  ignorePasswordManagers = false,
}: BaseFieldProps1) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  const touched = (formik.touched as any)?.[name];
  const error = (formik.errors as any)?.[name];
  const hasError = !!(touched && error);

  const bgColor = disabled ? "#f2f2f2" : (hover || focus ? "#fafafa" : "#f5f6fa");
  const borderColor = hasError
    ? "#747981"
    : disabled
      ? "#d1d5db"
      : hover || focus
        ? "#9ca3af"
        : "#c9ced6";

  const style: React.CSSProperties = {
    width: "100%",
    height: 39,
    borderRadius: 10,
    padding: "10px 12px",
    borderWidth: 2,
    borderStyle: "solid",
    borderColor,
    backgroundColor: bgColor,
    outline: "none",
    transition: "all .2s ease-in-out",
    boxShadow: "none",
    color: "#111827",
    cursor: disabled ? "not-allowed" : "text",
    opacity: 1, // don't fade when disabled
  };

  // Use provided id or default to name
  const inputId = id || name;

  /** Chrome often ignores `autocomplete="off"`; invalid tokens like `nope` suppress heuristics. */
  const isEmail = isEmailFieldName(name);
  const resolvedAutoComplete = resolveAutocomplete(autocomplete, isEmail);

  return (
    <div className="">
      {label && (
        <label className="form-label" htmlFor={inputId}>
          {label} {required && <span className="text-danger">*</span>}
        </label>
      )}

      <input
        id={inputId}
        name={name}
        type={type}
        className={`form-control ${hasError ? "is-invalid" : ""}`}
        style={style}
        value={(formik.values as any)[name] ?? ""}
        onChange={formik.handleChange}
        onFocus={() => !disabled && setFocus(true)}
        onBlur={(e) => {
          setFocus(false);
          formik.handleBlur(e);
        }}
        onMouseEnter={() => !disabled && setHover(true)}
        onMouseLeave={() => !disabled && setHover(false)}
        placeholder={placeholder}
        aria-invalid={hasError}
        disabled={disabled}     // ✅ lock on edit
        readOnly={disabled}     // keeps text selectable
        autoComplete={resolvedAutoComplete}
        spellCheck={spellCheck ?? (isEmail ? false : undefined)}
        autoCorrect={autoCorrect ?? (isEmail ? "off" : undefined)}
        data-lpignore={ignorePasswordManagers || isEmail ? "true" : undefined}
        data-bwignore={ignorePasswordManagers || isEmail ? "true" : undefined}
        {...(ignorePasswordManagers || isEmail ? { "data-1p-ignore": "true" } : {})}
      />

      {hasError ? <div className="invalid-feedback d-block">{String(error)}</div> : null}
    </div>
  );
}



export function UIReactSelectPOP({
  name,
  value,
  options,
  placeholder = "- Select -",
  menuMaxHeight = 200,
  menuPortalZIndex = 30,
  onChange,
  className,
}: UIReactSelectPOPProps) {
  // anchor the portal to this wrapper so the menu sits exactly under the field and fills its width
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const SELECT_HEIGHT_SM = 34;

  const styles = {
    ...centeredCommonStyles(false),
    control: (base: any, state: any) => ({
      ...centeredCommonStyles(false).control(base, state),
      minHeight: SELECT_HEIGHT_SM,
      height: SELECT_HEIGHT_SM,
      borderRadius: 8,
      borderColor: state.isFocused ? "#cbd5e1" : "#e7eaf3",
      boxShadow: "none",
      "&:hover": { borderColor: state.isFocused ? "#cbd5e1" : "#e7eaf3" },
      display: "flex",
      alignItems: "center",
    }),
    valueContainer: (base: any) => ({
      ...centeredCommonStyles(false).valueContainer(base),
      minHeight: SELECT_HEIGHT_SM,
      padding: "0 10px",
      display: "flex",
      alignItems: "center",       // <- vertical centering
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    singleValue: (base: any) => ({
      ...base,
      margin: 0,
      lineHeight: "20px",         // <- keep text visually centered
    }),
    placeholder: (base: any) => ({
      ...base,
      margin: 0,
      lineHeight: "20px",
    }),
    indicatorsContainer: (base: any) => ({
      ...centeredCommonStyles(false).indicatorsContainer(base),
      minHeight: SELECT_HEIGHT_SM,
      height: SELECT_HEIGHT_SM,
      alignItems: "center",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base: any) => ({
      ...base,
      padding: "0 8px",
    }),
    menuPortal: (base: any) => ({
      ...base,
      position: "absolute",
      zIndex: 30,
      width: "100%",
      left: 0,
    }),
    menu: (base: any) => ({
      ...centeredCommonStyles(false).menu(base),
      width: "100%",
      minWidth: "100%",
      maxWidth: "100%",
      maxHeight: 200,
      overflowY: "auto",
      marginTop: 4,
    }),
    menuList: (base: any) => ({
      ...base,
      paddingTop: 0,
      paddingBottom: 0,
    }),
  };


  return (
    <div className={`rs-pop-anchor ${className || ""}`} ref={anchorRef}>
      <Select<SimpleOptionWithDisabled, false>
        inputId={name}
        name={name}
        options={options}
        value={value}
        onChange={(opt) => onChange?.((opt ?? null) as SimpleOption | null)}
        placeholder={placeholder}
        classNamePrefix="rs"
        styles={styles}
        theme={commonTheme}
        components={{ DropdownIndicator }}
        /* anchor menu under this control */
        menuPortalTarget={anchorRef.current ?? undefined}
        menuPosition="absolute"
        menuPlacement="bottom"
        menuShouldScrollIntoView={false}
      />
    </div>
  );
}

export function UIReactSelect({
  formik,
  name,
  label,
  required,
  placeholder,
  options,
  id,
}: BaseFieldProps & { options: Array<{ value: string; label: string }> }) {
  const touched = (formik.touched as any)?.[name];
  const error = (formik.errors as any)?.[name];
  const hasError = !!(touched && error);

  // Use provided id or default to name
  const inputId = id || name;

  return (
    <div className="mb-3">
      <label className="form-label" htmlFor={inputId}>
        {label} {required && <span className="text-danger">*</span>}
      </label>

      <Select
        inputId={inputId}
        name={name}
        options={options}
        value={options?.find((o) => o.value === (formik.values as any)[name]) || null}
        onChange={(opt) => formik.setFieldValue(name, (opt as any)?.value || "")}
        onBlur={() => formik.setFieldTouched(name, true)}
        placeholder={placeholder || "Select..."}
        classNamePrefix="rs"
        styles={{ ...centeredCommonStyles(hasError), ...customSelectStyles }}
        theme={commonTheme}
        components={{ DropdownIndicator }}
      />

      {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
    </div>
  );
}

/**
 * UIReactSelectNew – formik Select with reliable value display and disabled support.
 * Use this when the selected value must show immediately (e.g. status on create).
 * Keeps UIReactSelect unchanged for existing usage.
 */
export function UIReactSelectNew({
  formik,
  name,
  label,
  required,
  placeholder,
  options,
  id,
  disabled,
}: BaseFieldProps & { options: Array<{ value: string; label: string }> }) {
  const touched = (formik.touched as any)?.[name];
  const error = (formik.errors as any)?.[name];
  const hasError = !!(touched && error);

  const inputId = id || name;
  const currentValue = (formik.values as any)?.[name];
  
  // Find the matching option - ensure it updates reactively when formik value changes
  // Use useMemo to prevent unnecessary recalculations, but ensure it updates when value changes
  const selectedOption = React.useMemo(() => {
    if (!currentValue || String(currentValue).trim() === "") return null;
    const val = String(currentValue).trim();
    const found = options?.find((o) => String(o.value).trim() === val);
    return found || null;
  }, [currentValue, options]);

  return (
    <div className="mb-3">
      <label className="form-label" htmlFor={inputId}>
        {label} {required && <span className="text-danger">*</span>}
      </label>

      <Select
        key={`select-${name}-${currentValue || 'empty'}`}
        inputId={inputId}
        name={name}
        options={options}
        value={selectedOption}
        onChange={(opt) => {
          const newValue = opt ? (opt as any)?.value : "";
          formik.setFieldValue(name, newValue);
        }}
        onBlur={() => formik.setFieldTouched(name, true)}
        placeholder={placeholder || "Select..."}
        classNamePrefix="rs"
        styles={{ ...centeredCommonStyles(hasError), ...customSelectStyles }}
        theme={commonTheme}
        components={{ DropdownIndicator }}
        isDisabled={disabled}
        isSearchable={false}
        isClearable={false}
      />

      {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
    </div>
  );
}

export function UIReactMultiSelect({
  formik,
  name,
  label,
  required,
  placeholder,
  options,
}: BaseFieldProps & { options: Array<{ value: string; label: string }> }) {
  const touched = (formik.touched as any)?.[name];
  const error = (formik.errors as any)?.[name];
  const hasError = !!(touched && error);

  const current: string[] = Array.isArray((formik.values as any)?.[name])
    ? ((formik.values as any)[name] as string[])
    : [];
  const value = options.filter((o) => current.includes(o.value));

  return (
    <div className="mb-3">
      <label className="form-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>

      <Select
        inputId={name}
        name={name}
        options={options}
        isMulti
        closeMenuOnSelect={false}
        value={value}
        onChange={(selected: any) => {
          const ids = (selected || []).map((o: any) => o.value);
          formik.setFieldValue(name, ids);
        }}
        onBlur={() => formik.setFieldTouched(name, true)}
        placeholder={placeholder || "Select..."}
        classNamePrefix="rs"
        styles={centeredMultiStyles(hasError)}
        theme={commonTheme}
        components={{ DropdownIndicator }}
      />

      {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
    </div>
  );
}

// NEW: flexible multi/single select with optional override
export function UIMultiSelectTeammber({
  formik,
  name,
  label,
  required,
  placeholder,
  options,
  isMulti = true,
  onChangeOverride,
}: BaseFieldProps & {
  options: Array<{ value: string; label: string }>;
  isMulti?: boolean;
  onChangeOverride?: (opt: any) => void;
}) {
  const touched = (formik.touched as any)?.[name];
  const error = (formik.errors as any)?.[name];
  const hasError = !!(touched && error);

  // current form value
  const rawVal = (formik.values as any)[name];

  // map current value -> react-select value
  const value = isMulti
    ? (Array.isArray(rawVal) ? options?.filter(o => rawVal.includes(o.value)) : [])
    : (options?.find(o => o.value === rawVal) || null);

  return (
    <div className="mb-3">
      <label className="form-label">
        {label} {required && <span className="text-danger">*</span>}
      </label>

      <Select
        inputId={name}
        name={name}
        options={options}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        value={value as any}
        onChange={(selected: any) => {
          if (onChangeOverride) {
            onChangeOverride(selected);
            return;
          }
          if (isMulti) {
            const ids = (selected || []).map((o: any) => o.value);
            formik.setFieldValue(name, ids);
          } else {
            formik.setFieldValue(name, (selected as any)?.value || "");
          }
        }}
        onBlur={() => formik.setFieldTouched(name, true)}
        placeholder={placeholder || "Select..."}
        classNamePrefix="rs"
        styles={isMulti ? centeredMultiStyles(hasError) : centeredCommonStyles(hasError)}
        theme={commonTheme}
        components={{ DropdownIndicator }}
      />

      {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
    </div>
  );
}



/** Input with exact screenshot look: rounded, light background, hover/focus border */
export function UIInput({
  formik,
  name,
  label,
  required,
  placeholder,
  disabled,
  id,
  autocomplete,
  spellCheck,
  autoCorrect,
  autoCapitalize,
  ignorePasswordManagers = false,
}: BaseFieldProps) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  const touched = (formik.touched as any)?.[name];
  const error = (formik.errors as any)?.[name];
  const hasError = !!(touched && error);

  // Dynamic colors for normal / hover+focus / error
  const bgColor = hover || focus ? "#fafafa" : "#f5f6fa";      // <— your hover bg
  const borderColor = hasError
    ? "#747981"
    : hover || focus
      ? "#9ca3af"                                                // <— your hover border
      : "#c9ced6";

  const style: React.CSSProperties = {
    width: "100%",
    height: 39,                                                // <— increased height
    borderRadius: 10,
    padding: "10px 12px",                                      // a bit more vertical padding
    borderWidth: 2,
    borderStyle: "solid",
    borderColor,
    backgroundColor: bgColor,
    outline: "none",
    transition: "all .2s ease-in-out",                        // <— your transition
    boxShadow: "none",
    color: "#111827",
  };

  // Use provided id or default to name
  const inputId = id || name;
  const isEmail = isEmailFieldName(name);
  const resolvedAutoComplete = resolveAutocomplete(autocomplete, isEmail);

  return (
    <div className="mb-3">
      <label className="form-label" htmlFor={inputId}>
        {label} {required && <span className="text-danger">*</span>}
      </label>

      <input
        id={inputId}
        name={name}
        className={`form-control ${hasError ? "is-invalid" : ""}`}
        style={style}
        value={(formik.values as any)[name] ?? ""}
        onChange={formik.handleChange}
        onFocus={() => setFocus(true)}
        onBlur={(e) => {
          setFocus(false);
          formik.handleBlur(e);
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        placeholder={placeholder}
        type="text"
        disabled={disabled}
        aria-invalid={hasError}
        autoComplete={resolvedAutoComplete}
        spellCheck={spellCheck ?? (isEmail ? false : undefined)}
        autoCorrect={autoCorrect ?? (isEmail ? "off" : undefined)}
        autoCapitalize={autoCapitalize ?? (isEmail ? "off" : undefined)}
        data-lpignore={ignorePasswordManagers || isEmail ? "true" : undefined}
        data-bwignore={ignorePasswordManagers || isEmail ? "true" : undefined}
      />

      {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
    </div>
  );
}

/** Native select matching the same visual style as inputs */
function UISelect({
  formik,
  name,
  label,
  required,
  placeholder,
  options,
}: BaseFieldProps & { options: Array<{ value: string; label: string }> }) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const touched = (formik.touched as any)?.[name];
  const error = (formik.errors as any)?.[name];

  const style: React.CSSProperties = {
    width: "100%",
    height: 39,
    borderRadius: 10,
    padding: "8px 12px",
    border: "1px solid",
    borderColor: focus ? "#7c8ac9" : hover ? "#b0b7c7" : "#c9ced6",
    background: "#f5f6fa",
    outline: "none",
    transition: "border-color .15s ease, box-shadow .15s ease",
    boxShadow: focus ? "0 0 0 2px rgba(86,107,255,.15)" : "none",
    color: "#111827",
    appearance: "none",
    backgroundImage:
      "linear-gradient(45deg, transparent 50%, #6b7280 50%),linear-gradient(135deg, #6b7280 50%, transparent 50%),linear-gradient(to right, #f5f6fa, #f5f6fa)",
    backgroundPosition: "calc(100% - 18px) calc(1em + 2px), calc(100% - 13px) calc(1em + 2px), 0 0",
    backgroundSize: "5px 5px, 5px 5px, 100% 100%",
    backgroundRepeat: "no-repeat",
  };

  return (
    <div className="mb-3">
      <label className="form-label ">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <select
        id={name}
        name={name}
        value={(formik.values as any)[name] ?? ""}
        className={`form-control ${touched && error ? "is-invalid" : ""}`}
        style={style}
        onChange={formik.handleChange}
        onBlur={(e) => {
          setFocus(false);
          formik.handleBlur(e);
        }}
        onFocus={() => setFocus(true)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <option value="">{placeholder || "- Select -"}</option>
        {options?.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {touched && error ? <div className="invalid-feedback d-block">{error}</div> : null}

      <style>{`
       .css-5rg3wt-singleValue{
       line-height: 31px !important; /* match input text centering */
        }
      `}</style>
    </div>
  );
}

/** React-select styled to visually match inputs from screenshot */
// export function UIReactSelect({
//     formik,
//     name,
//     label,
//     required,
//     placeholder,
//     options,
// }: BaseFieldProps & { options: Array<{ value: string; label: string }> }) {
//     const touched = (formik.touched as any)?.[name];
//     const error = (formik.errors as any)?.[name];
//     const hasError = !!(touched && error);

//     return (
//         <div className="mb-3">
//             <label className="form-label">
//                 {label} {required && <span className="text-danger">*</span>}
//             </label>

//             <Select
//                 inputId={name}
//                 name={name}
//                 options={options}
//                 value={options.find((o) => o.value === (formik.values as any)[name]) || null}
//                 onChange={(opt) => formik.setFieldValue(name, (opt as any)?.value || "")}
//                 onBlur={() => formik.setFieldTouched(name, true)}
//                 placeholder={placeholder || "Select..."}
//                 classNamePrefix="rs"
//                 styles={{
//                     control: (base, state) => {
//                         const focused = state.isFocused;
//                         return {
//                             ...base,
//                             minHeight: 39,
//                             height: 39,
//                             borderRadius: 10,
//                             backgroundColor: focused ? "#fafafa" : "#f5f6fa",
//                             borderWidth: 2,
//                             borderStyle: "solid",
//                             borderColor: hasError
//                                 ? "#747981"               // error
//                                 : focused
//                                     ? "#9ca3af"               // focus border
//                                     : "#c9ced6",              // normal
//                             boxShadow: "none",
//                             outline: "none",
//                             transition: "all .2s ease-in-out", // smooth hover/focus
//                             ":hover": {
//                                 borderColor: hasError ? "#747981" : "#9ca3af",
//                                 backgroundColor: "#fafafa",
//                             },
//                         };
//                     },
//                     dropdownIndicator: (provided: any, state: any) => ({
//                         ...provided,
//                         display: 'flex !important',
//                         opacity: 1,
//                         visibility: 'visible',
//                     }),
//                     indicatorSeparator: (provided: any, state: any) => ({
//                         ...provided,
//                         display: 'none',
//                     }),
//                     valueContainer: (b) => ({
//                         ...b,
//                         padding: "6px 12px",
//                         height: 48,
//                     }),
//                     indicatorsContainer: (b) => ({
//                         ...b,
//                         paddingRight: 10,
//                         height: 48,
//                     }),
//                     placeholder: (b) => ({ ...b, color: "#6b7280" }),
//                     singleValue: (b) => ({ ...b, color: "#111827" }),
//                     menu: (b) => ({ ...b, borderRadius: 10, overflow: "hidden" }),
//                 }}
//                 theme={(t) => ({
//                     ...t,
//                     colors: {
//                         ...t.colors,
//                         primary: "#9ca3af",       // match hover/focus border tone
//                         primary25: "rgba(156,163,175,0.15)",
//                         primary50: "rgba(156,163,175,0.25)",
//                     },
//                 })}
//                 components={{ DropdownIndicator }}
//             />

//             {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
//         </div>
//     );
// }

// export function UIReactMultiSelect({
//     formik,
//     name,
//     label,
//     required,
//     placeholder,
//     options,
// }: BaseFieldProps & { options: Array<{ value: string; label: string }> }) {
//     const touched = (formik.touched as any)?.[name];
//     const error = (formik.errors as any)?.[name];
//     const hasError = !!(touched && error);

//     // Ensure array form
//     const current: string[] = Array.isArray((formik.values as any)?.[name])
//         ? ((formik.values as any)[name] as string[])
//         : [];

//     const value = options.filter((o) => current.includes(o.value));

//     return (
//         <div className="mb-3">
//             <label className="form-label">
//                 {label} {required && <span className="text-danger">*</span>}
//             </label>

//             <Select
//                 inputId={name}
//                 name={name}
//                 options={options}
//                 isMulti
//                 closeMenuOnSelect={false}
//                 value={value}
//                 onChange={(selected: any) => {
//                     const ids = (selected || []).map((o: any) => o.value);
//                     formik.setFieldValue(name, ids);
//                 }}
//                 onBlur={() => formik.setFieldTouched(name, true)}
//                 placeholder={placeholder || "Select..."}
//                 classNamePrefix="rs"
//                 styles={{
//                     control: (base, state) => {
//                         const focused = state.isFocused;
//                         return {
//                             ...base,
//                             minHeight: 39,
//                             height: 39,              // allow chips to wrap for multi
//                             borderRadius: 10,
//                             backgroundColor: focused ? "#fafafa" : "#f5f6fa",
//                             borderWidth: 2,
//                             borderStyle: "solid",
//                             borderColor: hasError
//                                 ? "#747981"                 // error
//                                 : focused
//                                     ? "#9ca3af"                 // focus
//                                     : "#c9ced6",                // normal
//                             boxShadow: "none",
//                             outline: "none",
//                             transition: "all .2s ease-in-out",
//                             ":hover": {
//                                 borderColor: hasError ? "#747981" : "#9ca3af",
//                                 backgroundColor: "#fafafa",
//                             },
//                         };
//                     },
//                     dropdownIndicator: (provided: any, state: any) => ({
//                         ...provided,
//                         display: 'flex !important',
//                         opacity: 1,
//                         visibility: 'visible',
//                     }),
//                     indicatorSeparator: (provided: any, state: any) => ({
//                         ...provided,
//                         display: 'none',
//                     }),
//                     valueContainer: (b) => ({
//                         ...b,
//                         padding: "6px 12px",
//                     }),
//                     indicatorsContainer: (b) => ({
//                         ...b,
//                         paddingRight: 10,
//                         minHeight: 48,
//                     }),
//                     placeholder: (b) => ({ ...b, color: "#6b7280" }),
//                     singleValue: (b) => ({ ...b, color: "#111827" }),
//                     menu: (b) => ({ ...b, borderRadius: 10, overflow: "hidden" }),
//                     // nice compact tags
//                     multiValue: (b) => ({
//                         ...b,
//                         backgroundColor: "rgba(156,163,175,0.15)",
//                     }),
//                     multiValueLabel: (b) => ({
//                         ...b,
//                         color: "#111827",
//                         paddingRight: 2,
//                     }),
//                     multiValueRemove: (b) => ({
//                         ...b,
//                         ":hover": { backgroundColor: "rgba(156,163,175,0.25)", cursor: "pointer" },
//                     }),
//                 }}
//                 theme={(t) => ({
//                     ...t,
//                     colors: {
//                         ...t.colors,
//                         primary: "#9ca3af",         // match hover/focus border tone
//                         primary25: "rgba(156,163,175,0.15)",
//                         primary50: "rgba(156,163,175,0.25)",
//                     },
//                 })}
//                 components={{ DropdownIndicator }}

//             />

//             {hasError ? <div className="invalid-feedback d-block">{error}</div> : null}
//         </div>
//     );
// }

export function UICheckbox({
  formik,
  name,
  label,
}: {
  formik: ReturnType<typeof useFormik<any>>;
  name: string;
  label: string;
}) {
  return (
    <label className="form-check d-flex align-items-center gap-2 me-4">
      {/* CHECKBOX (not radio) */}
      <input
        type="checkbox"
        className="form-check-input"
        name={name}
        id={name}
        checked={!!(formik.values as any)[name]}
        onChange={formik.handleChange}
        style={{ width: 16, height: 16, cursor: "pointer" }}
      />
      <span className="form-check-label" style={{ cursor: "pointer" }}>
        {label}
      </span>
    </label>
  );
}